import { Box } from '@mui/material';
import classNames from 'classnames';
import React, { memo, useState } from 'react';

import { withDimensions } from '../../util/contextHelpers';
import Handle from './Handle';
import Track from './Track';

const rootSx = {
  position: 'relative',
  margin: '0 10px',
  width: 'calc(100% - 20px)',
  height: '24px',
};

/**
 * Inner RangeSlider that receives `dimensions` from the withDimensions HOC.
 *
 * @param {Object} props
 * @param {number[]} props.handles handle values in ascending order, within [min, max]
 * @param {number} [props.min=0] minimum value
 * @param {number} [props.max=10000000] maximum value
 * @param {number} [props.step=1] step granularity
 * @param {Object} props.dimensions measured dimensions, shape `{ width, height }`
 * @param {Function} props.onChange called with the updated handles array
 */
const RangeSliderComponent = ({
  handles,
  min = 0,
  max = 10000000,
  step = 1,
  dimensions,
  onChange,
}) => {
  handles.forEach((h, index) => {
    if (h < min || h > max || (index < handles.length - 1 && h > handles[index + 1])) {
      throw new Error(
        'RangeSlider error: handles need to be given in ascending order and they need to be within min and max values'
      );
    }
  });

  const [activeHandle, setActiveHandle] = useState(0);

  const toPosition = (value) => {
    const width = dimensions.width;
    const valueOffset = value - min;
    const scale = max - min;
    return Math.round((valueOffset / scale) * width);
  };

  const toValue = (position) => {
    const width = dimensions.width;
    const scale = max - min;
    const value = Math.round((position / width) * scale) + min;
    return Math.ceil(value / step) * step;
  };

  const handleChange = (position, handleIndex) => {
    onChange(Object.assign([...handles], { [handleIndex]: position }));
  };

  return (
    <Track handles={handles} valueToPosition={toPosition}>
      {handles.map((h, index) => (
        <Handle
          changeActive={() => setActiveHandle(index)}
          key={index}
          max={index === handles.length - 1 ? max : handles[index + 1]}
          min={index === 0 ? min : handles[index - 1]}
          onChange={(value) => handleChange(value, index)}
          positionToValue={toValue}
          sx={activeHandle === index ? { zIndex: 1 } : undefined}
          value={h}
          valueToPosition={toPosition}
        />
      ))}
    </Track>
  );
};

const RangeSliderComponentWithDimensions = withDimensions(memo(RangeSliderComponent));

const RangeSlider = ({ rootClassName = null, className = null, ...rest }) => {
  const classes = classNames(rootClassName, className);
  return (
    <Box className={classes} sx={rootClassName ? undefined : rootSx}>
      <RangeSliderComponentWithDimensions {...rest} />
    </Box>
  );
};

export default RangeSlider;
